// Content schema of the editor: which elements and attributes survive loading and saving.
//
// Object content is shared with Admin2 (same HugeRTE version, see Admin2 doc/rich_text_editor.md)
// and contains two kinds of price tables:
// - pricing tables from the basis3PricingTable plugin below (figure.pricingtable, caption with
//   div/span, id/headers);
// - legacy price tables moved from the former price_html field. They rely on the presentational
//   attributes border, cellpadding, cellspacing, width and inline style.
// The legacy attributes are preserved only. Users cannot set them: the table plugin is not enabled
// on any page, pasting is reduced to plain text, and only staff get the code view. The list matches
// what Admin2's server-side sanitizer (SanitizesHtmlFields) keeps; align and valign are left out
// because the server drops them anyway. Interim solution until both editors share one schema.
//
// Price tables must render cleanly and mobile-friendly in every frontend that shows object
// content (basis3, CMS2, MS4/Arya). Check those renderers before changing the markup or this list.
const LEGACY_TABLE_ATTRIBUTES = 'border|cellpadding|cellspacing|width';
const LEGACY_CELL_ATTRIBUTES = 'style|width';

const EDITOR_CONTENT_SCHEMA = Object.freeze({
  extended_valid_elements: [
    'figure[class|id]',
    `table[class|id|style|role|aria-describedby|${LEGACY_TABLE_ATTRIBUTES}]`,
    'caption[class|id|style]',
    'thead[class|id]',
    'tbody[class|id]',
    'tfoot[class|id]',
    'tr[class|id]',
    `th[class|id|scope|headers|rowspan|colspan|${LEGACY_CELL_ATTRIBUTES}]`,
    `td[class|id|headers|rowspan|colspan|${LEGACY_CELL_ATTRIBUTES}]`,
    'div[class|id|style]',
    'span[class|id|style]'
  ].join(','),
  valid_children: '+caption[div|span|#text],+figure[table|figcaption],+table[caption|thead|tbody|tfoot|tr],+thead[tr],+tbody[tr],+tfoot[tr],+tr[th|td]',
  custom_elements: 'figure',
  // Encode like Admin2 and its server-side sanitizer (Nokogiri): characters as-is, only
  // &amp; &lt; &gt; &nbsp; as entities. HugeRTE's default named list stores ä as &auml;, so every
  // save by the other side showed up as a change.
  entity_encoding: 'named',
  entities: '160,nbsp'
});

const BLOCK_PASTE_ELEMENTS = new Set([
  'address',
  'article',
  'aside',
  'blockquote',
  'caption',
  'div',
  'dl',
  'dt',
  'dd',
  'figcaption',
  'figure',
  'footer',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'header',
  'li',
  'main',
  'nav',
  'ol',
  'p',
  'pre',
  'section',
  'table',
  'tbody',
  'td',
  'tfoot',
  'th',
  'thead',
  'tr',
  'ul'
]);

const IGNORED_PASTE_ELEMENTS = new Set([
  'audio',
  'canvas',
  'iframe',
  'noscript',
  'object',
  'script',
  'style',
  'svg',
  'textarea',
  'video'
]);

function appendPlainTextFromNode(node, chunks) {
  if (node.nodeType === 3) {
    chunks.push(node.nodeValue || '');
    return;
  }

  if (node.nodeType !== 1) {
    return;
  }

  const tagName = node.tagName.toLowerCase();

  if (IGNORED_PASTE_ELEMENTS.has(tagName)) {
    return;
  }

  if (tagName === 'br') {
    chunks.push('\n');
    return;
  }

  Array.from(node.childNodes).forEach(function (childNode) {
    appendPlainTextFromNode(childNode, chunks);
  });

  if (BLOCK_PASTE_ELEMENTS.has(tagName)) {
    chunks.push('\n\n');
  }
}

function normalizePlainTextFromHtml(root) {
  const chunks = [];
  Array.from(root.childNodes).forEach(function (node) {
    appendPlainTextFromNode(node, chunks);
  });

  return chunks.join('')
    .replace(/\u00a0/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n[ \t]+/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function plainTextToHtml(text) {
  const normalizedText = String(text || '').replace(/\r\n?/g, '\n');
  if (!normalizedText.trim()) {
    return '';
  }

  return normalizedText
    .split(/\n{2,}/)
    .map(function (paragraph) {
      return '<p>' + paragraph.split('\n').map(escapeHtml).join('<br>') + '</p>';
    })
    .join('');
}

function stripFormattingFromHtml(html) {
  if (!html || typeof document === 'undefined') {
    return html;
  }

  const container = document.createElement('div');
  container.innerHTML = html;
  return plainTextToHtml(normalizePlainTextFromHtml(container));
}

function stripFormattingFromNode(root) {
  if (!root || typeof root.innerHTML !== 'string') {
    return;
  }

  root.innerHTML = stripFormattingFromHtml(root.innerHTML);
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escapeHtmlWithLineBreaks(value) {
  return escapeHtml(value).replace(/\r?\n/g, '<br>');
}

function getTextContentWithLineBreaks(element) {
  if (!element) {
    return '';
  }

  const clone = element.cloneNode(true);
  clone.querySelectorAll('br').forEach(function (br) {
    br.replaceWith('\n');
  });
  return clone.textContent.trim();
}

function slugify(value) {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function findPricingFigureFromNode(editor, node) {
  return editor.dom.getParent(node, 'figure.pricingtable');
}

function lockPricingFigure(figure) {
  if (!figure) {
    return;
  }
  figure.setAttribute('contenteditable', 'false');
  figure.setAttribute('data-mce-contenteditable', 'false');
}

function lockAllPricingFigures(editor) {
  const body = editor.getBody();
  if (!body) {
    return;
  }
  body.querySelectorAll('figure.pricingtable').forEach(function (figure) {
    lockPricingFigure(figure);
  });
}

function syncRowCellCount(state) {
  const columnCount = state.columns.length;
  state.rows.forEach(function (row) {
    while (row.cells.length < columnCount) {
      row.cells.push('-');
    }
    if (row.cells.length > columnCount) {
      row.cells = row.cells.slice(0, columnCount);
    }
  });
}

// Label for a row's price input: the column name once set, otherwise its position.
function columnCellLabel(column, index) {
  const name = String(column?.name ?? '').trim();
  return name || 'Spalte ' + (index + 1) + ' (td)';
}

function createDefaultState() {
  const state = {
    id: 'preise',
    caption: 'Beispielzimmer',
    subCaption: '',
    columns: [
      {name: 'Beispielspalte', info: ''},
    ],
    rows: [
      {header: 'Beispielzimmer', cells: ['80 €', '110 €']},
    ]
  };
  syncRowCellCount(state);
  return state;
}

function parsePricingFigure(figure) {
  const mainTable = figure.querySelector('table');
  if (!mainTable) {
    return null;
  }

  const state = {
    id: 'preise',
    caption: '',
    subCaption: '',
    columns: [],
    rows: []
  };

  const captionTitle = mainTable.querySelector('caption .caption-title');
  const captionLead = mainTable.querySelector('caption .caption-lead');
  state.caption = captionTitle ? captionTitle.textContent.trim() : '';
  state.subCaption = captionLead ? captionLead.textContent.trim() : '';

  const firstHeaderRow = mainTable.querySelector('thead tr:first-child');
  const secondHeaderRow = mainTable.querySelector('thead tr:nth-child(2)');

  if (firstHeaderRow) {
    const columnHeaders = Array.from(firstHeaderRow.querySelectorAll('th'));
    columnHeaders.forEach(function (th, index) {
      const periodCell = secondHeaderRow ? secondHeaderRow.querySelectorAll('td')[index] : null;
      state.columns.push({
        name: th.textContent.trim(),
        info: getTextContentWithLineBreaks(periodCell)
      });
    });

    if (columnHeaders.length > 0) {
      const firstColumnId = columnHeaders[0].id || '';
      const firstColumnSlug = slugify(state.columns[0]?.name || '');
      const suffix = firstColumnSlug ? '-' + firstColumnSlug : '';
      if (firstColumnId && suffix && firstColumnId.endsWith(suffix)) {
        state.id = firstColumnId.slice(0, -suffix.length) || 'preise';
      }
    }
  }

  mainTable.querySelectorAll('tbody tr').forEach(function (tr) {
    const th = tr.querySelector('th');
    const tds = Array.from(tr.querySelectorAll('td'));
    state.rows.push({
      header: th ? th.textContent.trim() : '',
      cells: tds.map(function (td) {
        return td.textContent.trim();
      })
    });
  });

  if (state.columns.length === 0) {
    state.columns = [{name: 'Preis', info: ''}];
  }
  if (state.rows.length === 0) {
    state.rows = [{header: '', cells: []}];
  }
  syncRowCellCount(state);
  return state;
}

function buildPricingHtml(state) {
  const blockId = slugify(state.id);
  if (!blockId) {
    throw new Error('Bitte eine gültige Tabellen-ID angeben.');
  }
  const columns = state.columns.filter(function (column) {
    return String(column.name || '').trim() !== '';
  });

  if (columns.length === 0) {
    throw new Error('Mindestens eine Spalte ist erforderlich.');
  }

  const rows = state.rows.length > 0 ? state.rows : [{header: '', cells: []}];
  rows.forEach(function (row) {
    while (row.cells.length < columns.length) {
      row.cells.push('-');
    }
    if (row.cells.length > columns.length) {
      row.cells = row.cells.slice(0, columns.length);
    }
  });

  const hasInfo = columns.some(function (column) {
    return String(column.info || '').trim() !== '';
  });

  let html = '';
  html += '<figure class="wp-block-table pricingtable">';
  // "full" opts the table into MS4/Arya's mobile stacking (resources/js/utils/responsiveTable.js),
  // like the legacy price tables. Keep in sync with Admin2's hugerte_pricing_table.js.
  html += '<table class="full">';
  html += '<caption>';
  html += '<div class="caption-title h3">' + escapeHtml(state.caption) + '</div>';
  html += '<span class="caption-lead">' + escapeHtml(state.subCaption) + '</span>';
  html += '</caption>';
  html += '<thead>';
  html += '<tr>';
  html += '<td class="empty" rowspan="' + (hasInfo ? '2' : '1') + '"></td>';
  columns.forEach(function (column) {
    const columnSlug = slugify(column.name) || 'column';
    html += '<th id="' + escapeHtml(blockId + '-' + columnSlug) + '">' + escapeHtml(column.name) + '</th>';
  });
  html += '</tr>';
  if (hasInfo) {
    html += '<tr class="white-space-pre-line">';
    columns.forEach(function (column) {
      const columnSlug = slugify(column.name) || 'column';
      const headerId = blockId + '-' + columnSlug;
      html += '<td headers="' + escapeHtml(headerId) + '" id="' + escapeHtml(headerId + '-periods') + '">' + escapeHtmlWithLineBreaks(column.info) + '</td>';
    });
    html += '</tr>';
  }
  html += '</thead>';
  html += '<tbody>';
  rows.forEach(function (row) {
    const rowSlug = slugify(row.header) || 'row';
    const rowId = blockId + '-' + rowSlug;
    html += '<tr>';
    html += '<th id="' + escapeHtml(rowId) + '">' + escapeHtml(row.header) + '</th>';
    row.cells.forEach(function (cellValue, iCol) {
      const columnSlug = slugify(columns[iCol].name) || 'column';
      const columnHeader = blockId + '-' + columnSlug;
      html += '<td headers="' + escapeHtml(rowId + ' ' + columnHeader) + '">' + escapeHtml(cellValue) + '</td>';
    });
    html += '</tr>';
  });
  html += '</tbody>';
  html += '</table>';
  html += '</figure>';

  return html;
}

function moveItem(list, from, to) {
  if (to < 0 || to >= list.length) {
    return;
  }
  const item = list.splice(from, 1)[0];
  list.splice(to, 0, item);
}

function registerBasis3PricingTablePlugin() {
  const rte = typeof window !== 'undefined' ? (window.hugerte || window.tinymce) : null;
  if (!rte || !rte.PluginManager) {
    return;
  }
  if (rte.PluginManager.get('basis3PricingTable')) {
    return;
  }

  rte.PluginManager.add('basis3PricingTable', function (editor) {
    let selectedFigure = null;

    function setSelectedFigure(figure) {
      if (selectedFigure === (figure || null)) {
        return;
      }
      if (selectedFigure && selectedFigure !== figure) {
        editor.dom.removeClass(selectedFigure, 'pricingtable-selected');
      }
      selectedFigure = figure || null;
      if (selectedFigure) {
        editor.dom.addClass(selectedFigure, 'pricingtable-selected');
      }
    }

    function selectFigure(figure) {
      lockPricingFigure(figure);
      setSelectedFigure(figure);
      editor.nodeChanged();
    }

    function openNewTable() {
      const openCallback = window.__basis3PricingTableOpenCallback;
      if (typeof openCallback !== 'function') {
        window.alert('Der Dialog für die Preistabelle ist nicht verfügbar.');
        return;
      }
      openCallback(editor, createDefaultState(), null);
    }

    function editSelectedTable() {
      const figure = findPricingFigureFromNode(editor, editor.selection.getNode());
      const openCallback = window.__basis3PricingTableOpenCallback;

      if (typeof openCallback !== 'function') {
        window.alert('Der Dialog für die Preistabelle ist nicht verfügbar.');
        return;
      }

      if (!figure) {
        window.alert('Bitte wählen Sie eine Preistabelle zum Bearbeiten aus.');
        return;
      }

      const parsed = parsePricingFigure(figure);
      if (!parsed) {
        window.alert('Das Markup der Preistabelle konnte nicht gelesen werden.');
        return;
      }
      selectFigure(figure);
      openCallback(editor, parsed, figure);
    }

    editor.on('init SetContent Change Undo Redo', function () {
      lockAllPricingFigures(editor);
      if (selectedFigure && !editor.dom.isChildOf(selectedFigure, editor.getBody())) {
        setSelectedFigure(null);
      }
    });

    editor.on('NodeChange', function () {
      const figure = findPricingFigureFromNode(editor, editor.selection.getNode());
      if (figure) {
        lockPricingFigure(figure);
        setSelectedFigure(figure);
      } else {
        setSelectedFigure(null);
      }
    });

    editor.on('keydown', function (event) {
      const figure = selectedFigure || findPricingFigureFromNode(editor, editor.selection.getNode());

      if (event.key === 'Enter' && figure) {
        event.preventDefault();
        const paragraph = editor.dom.create('p');
        paragraph.innerHTML = '<br data-mce-bogus="1">';
        editor.dom.insertAfter(paragraph, figure);
        editor.selection.setCursorLocation(paragraph, 0);
        setSelectedFigure(null);
        editor.nodeChanged();
        editor.fire('change');
        return;
      }

      if (event.key !== 'Backspace' && event.key !== 'Delete') {
        return;
      }

      if (!figure) {
        return;
      }

      event.preventDefault();
      const range = editor.dom.createRng();
      range.setStartBefore(figure);
      range.setEndBefore(figure);
      editor.selection.setRng(range);
      editor.dom.remove(figure);
      setSelectedFigure(null);
      editor.nodeChanged();
      editor.fire('change');
    });

    editor.ui.registry.addButton('basis3PricingTable', {
      text: 'Neue Preistabelle',
      tooltip: 'Neue Preistabelle einfügen',
      onAction: openNewTable
    });

    editor.ui.registry.addMenuItem('basis3PricingTable', {
      text: 'Neue Preistabelle',
      onAction: openNewTable
    });

    editor.ui.registry.addButton('basis3PricingTableEdit', {
      text: 'Preistabelle bearbeiten',
      tooltip: 'Preistabelle bearbeiten',
      onAction: editSelectedTable
    });

    editor.ui.registry.addContextToolbar('basis3PricingTableContext', {
      predicate: function (node) {
        return !!findPricingFigureFromNode(editor, node);
      },
      items: 'basis3PricingTableEdit',
      position: 'node',
      scope: 'node'
    });
  });
}

export {
  EDITOR_CONTENT_SCHEMA,
  stripFormattingFromHtml,
  stripFormattingFromNode,
  escapeHtml,
  escapeHtmlWithLineBreaks,
  getTextContentWithLineBreaks,
  slugify,
  findPricingFigureFromNode,
  lockPricingFigure,
  lockAllPricingFigures,
  syncRowCellCount,
  columnCellLabel,
  createDefaultState,
  parsePricingFigure,
  buildPricingHtml,
  moveItem,
  registerBasis3PricingTablePlugin
};
